import React from 'react';
import { useMailProvider } from './MailProviderContext';
import type { MailThread } from '../../lib/stores/mail/types';
import { Image as ImageIcon, FileText, FileSpreadsheet, File as FileIcon } from 'lucide-react';

// ─── Etichette ───

const SYSTEM_HIDE = new Set([
  'INBOX', 'SENT', 'UNREAD', 'STARRED', 'DRAFT', 'TRASH', 'SPAM', 'CHAT', 'CATEGORY_PERSONAL', 'IMPORTANT',
]);

const CATEGORY_META: Record<string, { name: string; color: string }> = {
  CATEGORY_PROMOTIONS: { name: 'Promozioni', color: '#16A34A' },
  CATEGORY_SOCIAL: { name: 'Social', color: '#2383E2' },
  CATEGORY_UPDATES: { name: 'Aggiornamenti', color: '#D9730D' },
  CATEGORY_FORUMS: { name: 'Forum', color: '#7C3AED' },
};

interface DisplayLabel {
  key: string;
  name: string;
  bg: string;
  fg: string;
}

function unionLabelIds(thread: MailThread): string[] {
  const set = new Set<string>();
  for (const m of thread.messages) for (const id of (m.label_ids || [])) set.add(id);
  return [...set];
}

export function LabelChips({ thread }: { thread: MailThread }) {
  const { useStore } = useMailProvider();
  const labelsMap = useStore(s => s.labels);
  const ids = unionLabelIds(thread);

  const display: DisplayLabel[] = [];
  for (const id of ids) {
    if (SYSTEM_HIDE.has(id)) continue;
    const cat = CATEGORY_META[id];
    if (cat) {
      display.push({ key: id, name: cat.name, bg: `${cat.color}1A`, fg: cat.color });
      continue;
    }
    const l = labelsMap[id];
    if (l && l.type === 'user') {
      display.push({
        key: id,
        name: l.name,
        bg: l.backgroundColor || '#EDEDEC',
        fg: l.textColor || '#37352F',
      });
    }
  }

  if (display.length === 0) return null;

  return (
    <>
      {display.map(d => (
        <span
          key={d.key}
          className="flex-shrink-0 text-[10px] px-1.5 py-0.5 rounded truncate max-w-[120px]"
          style={{ backgroundColor: d.bg, color: d.fg }}
          title={d.name}
        >
          {d.name}
        </span>
      ))}
    </>
  );
}

// ─── Chip allegati ───

interface AttachmentInfo {
  filename: string;
  mimeType: string;
}

function collectAttachments(thread: MailThread): AttachmentInfo[] {
  const seen = new Set<string>();
  const out: AttachmentInfo[] = [];
  for (const m of thread.messages) {
    for (const a of (m.attachments_metadata || [])) {
      if (!a.filename || seen.has(a.filename)) continue;
      seen.add(a.filename);
      out.push({ filename: a.filename, mimeType: a.mimeType || '' });
    }
  }
  return out;
}

function attachmentIcon(mime: string, filename: string) {
  const f = filename.toLowerCase();
  if (mime.startsWith('image/') || /\.(png|jpe?g|gif|webp|svg|heic)$/.test(f)) {
    return <ImageIcon className="h-3 w-3 text-[#2383E2]" />;
  }
  if (mime === 'application/pdf' || f.endsWith('.pdf')) {
    return <FileText className="h-3 w-3 text-[#DC2626]" />;
  }
  if (/sheet|excel|csv/.test(mime) || /\.(xlsx?|csv)$/.test(f)) {
    return <FileSpreadsheet className="h-3 w-3 text-[#16A34A]" />;
  }
  return <FileIcon className="h-3 w-3 text-[#787774]" />;
}

export function AttachmentChips({ thread }: { thread: MailThread }) {
  const atts = collectAttachments(thread);
  if (atts.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
      {atts.slice(0, 4).map((a, i) => (
        <span
          key={`${a.filename}-${i}`}
          className="inline-flex items-center gap-1 max-w-[160px] px-1.5 py-0.5 rounded-full border border-[rgba(55,53,47,0.12)] dark:border-gray-700 bg-white dark:bg-gray-900"
          title={a.filename}
        >
          {attachmentIcon(a.mimeType, a.filename)}
          <span className="text-[10px] text-[#37352F] dark:text-white/70 truncate">{a.filename}</span>
        </span>
      ))}
      {atts.length > 4 && (
        <span className="text-[10px] text-[#9B9A97] dark:text-white/40">+{atts.length - 4}</span>
      )}
    </div>
  );
}
